import {
  ArrowButton,
  ButtonsContainer,
  LayoutContainer,
  LoadingContainer,
  PageContainer,
  RotatingIcon,
  StatisticsFiltersStyles,
  StyledDismissButton,
  ViewPageContainer
} from 'app/components/common/common-styles';
import { BodyText, FlexColumn, FlexRow, HeaderText } from '@make-software/cspr-design';
import React from 'react';
import { StyledTableHeader } from 'app/components/common/layout/styled-elements';
import { SmoothCollapse } from 'app/components/management/apps/deals/list/smooth-collapse';
import IconArrowDown from 'assets/icons/ic-arrow-down.svg';
import { TaskTypeAdapter, TaskTypeFilter } from 'app/components/filters/task-type-filter';
import { useUrlFilterController } from 'app/components/filters/url-filters';
import {
  EventsGroupBy,
  EventsHistogram,
  EventType,
  EventTypeLabels,
  SubjectType,
  SubjectTypeLabels
} from 'app/console-api/statistics/types';
import { getEventsHistogram } from 'app/console-api/statistics/events-histogram';
import { EntityCountChart } from 'app/components/common/event-chart/entity-count-chart';
import { RequestCountChartHeader } from 'app/components/common/event-chart/chart-header';
import { FailedToFetch } from 'app/components/common/table/table-error';
import { ErrorResult } from 'app/organizations-api/types';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import { AppsFilter, AppsFilterAdapter } from 'app/components/filters/apps-filter';
import { App, getAppsList } from 'app/console-api';
import { GraphComponentDataFormatType } from 'app/components/common/charts/graph-component';
import { getPluginsList, Plugins } from 'app/console-api/plugins/plugins';
import { GroupByFilterAdapter } from 'app/components/filters/group-by-filter';
import { Calendar } from 'app/components/common/calendar/calendar';
import { EventTypeFilter, EventTypeFilterAdapter } from 'app/components/filters/event-type-filter';
import { DateRangeFilterAdapter } from 'app/components/filters/date-range-filter';
import {
  EventOriginFilter,
  EventOriginFilterAdapter
} from 'app/components/filters/event-origin-filter';

type LegendItem = {
  id: string; // group id (e.g., appID)
  name: string; // human label (e.g., app name)
  count: number; // total from histogram.totalByGroup
};

export interface GraphDataItemType {
  date: string;

  [key: string]: string | number;
}

export const StatisticsPage = () => {
  const [isOpen, setIsOpen] = React.useState(true);
  const filterController = useUrlFilterController();
  const [histogramData, setHistogramData] = React.useState<EventsHistogram | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<ErrorResult | null>(null);
  const [appsList, setAppsList] = React.useState<App[]>([]);
  const [plugins, setPlugins] = React.useState<Plugins[]>([]);

  // id -> name map for legend labels
  const appNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    (appsList ?? []).forEach((a) => m.set(a.id, a.name));
    return m;
  }, [appsList]);

  const pluginNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    (plugins ?? []).forEach((p) => m.set(String(p.id), p.name));
    return m;
  }, [plugins]);

  // current groupBy from URL/controller (fallback to event_type)
  const currentGroupBy: EventsGroupBy =
    (filterController.get(GroupByFilterAdapter) as EventsGroupBy) ?? EventsGroupBy.EventType;

  // unified name resolver per group type
  const resolveLegendName = React.useCallback(
    (groupId: string): string => {
      switch (currentGroupBy) {
        case EventsGroupBy.EventsOrigin:
          return SubjectTypeLabels[groupId as SubjectType.Task] ?? 'nil';
        case EventsGroupBy.TaskType:
          return pluginNameById.get(groupId) ?? 'Unknown Plugin';
        case EventsGroupBy.App:
          return appNameById.get(groupId) ?? groupId;
        case EventsGroupBy.EventType:
          return EventTypeLabels[groupId as EventType] ?? groupId;
        default:
          return groupId;
      }
    },
    [currentGroupBy, pluginNameById, appNameById]
  );

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // fetch both concurrently
    Promise.all([
      getAppsList({ pageSize: Number(process.env.REACT_APP_PAGE_SIZE_ITEMS), currentPage: 1 }),
      getEventsHistogram(filterController),
      getPluginsList()
    ])
      .then(([appsRes, hist, plugins]) => {
        if (cancelled) return;
        setAppsList(appsRes.data || []);
        setHistogramData(hist);
        setPlugins(plugins || []);
      })
      .catch((e) => !cancelled && setError(e))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [filterController]);

  const { series } = React.useMemo(() => {
    if (!histogramData) return { series: [] };

    // Build zero-filled template from every group present in totals
    const allGroupKeys = new Set<string>(
      (histogramData.totalByGroup ?? []).map((x) => String(x.key))
    );

    // rows for the chart: one per bucket with zeros for all groups
    const rows: GraphDataItemType[] = histogramData.buckets.map((b) => {
      const row: GraphDataItemType = { date: b.ts };
      allGroupKeys.forEach((k) => (row[k] = 0));
      (b.groups ?? []).forEach((g) => {
        row[String(g.key)] = g.value;
      });
      return row;
    });

    // legend with counts from totalByGroup; label from apps list (fallback to id)
    const totals = histogramData.totalByGroup ?? [];

    const legend: LegendItem[] = totals
      .map((g) => {
        const id = String(g.key);
        return {
          id,
          name: resolveLegendName(id),
          count: g.value
        };
      })
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

    // chart series (one per group)
    const series: GraphComponentDataFormatType[] = legend.map((l) => ({
      graphKey: l.id,
      xAxisValueKey: l.id,
      legendLabel: l.name,
      legendTotal: l.count,
      data: rows,
      initiallySelected: true
    }));

    return { series, legendItems: legend };
  }, [histogramData, appNameById, pluginNameById, resolveLegendName]);

  const onResetFilters = () => {
    filterController.resetToDefault([
      DateRangeFilterAdapter,
      EventOriginFilterAdapter,
      AppsFilterAdapter,
      EventTypeFilterAdapter,
      GroupByFilterAdapter
    ]);
  };

  return (
    <LayoutContainer>
      <StyledTableHeader justify={'space-between'}>
        <HeaderText size={1} scale={'xs'}>
          Analytics
        </HeaderText>
      </StyledTableHeader>
      <PageContainer>
        <ViewPageContainer>
          <FlexRow justify="space-between" gap={8}>
            <BodyText scale={'xl'} size={1} variation="black" noWrap>
              Filters
            </BodyText>
            <ArrowButton
              onClick={() => setIsOpen((v) => !v)}
              aria-expanded={isOpen}
              aria-label={isOpen ? 'Collapse details' : 'Expand details'}
            >
              <RotatingIcon src={IconArrowDown} $open={isOpen} />
            </ArrowButton>
          </FlexRow>

          <SmoothCollapse isOpen={isOpen}>
            <FlexColumn gap={12} style={{ paddingTop: 36 }}>
              <Calendar style={{ maxWidth: 650 }}></Calendar>
              <AppsFilter apps={appsList} styles={StatisticsFiltersStyles()}></AppsFilter>
              <EventOriginFilter styles={StatisticsFiltersStyles()}></EventOriginFilter>
              <EventTypeFilter styles={StatisticsFiltersStyles()}></EventTypeFilter>
              <TaskTypeFilter styles={StatisticsFiltersStyles()}></TaskTypeFilter>
              <ButtonsContainer gap={10} style={{ maxWidth: 350 }}>
                <StyledDismissButton color={'secondaryBlue'} onClick={onResetFilters}>
                  Reset to default
                </StyledDismissButton>
              </ButtonsContainer>
            </FlexColumn>
          </SmoothCollapse>
        </ViewPageContainer>
      </PageContainer>
      {loading ? (
        <PageContainer>
          <LoadingContainer>
            <SkeletonLoader height={'600px'} />
          </LoadingContainer>
        </PageContainer>
      ) : (
        <EntityCountChart
          chartHeader={
            <RequestCountChartHeader
              title={'Events Count'}
              totalRequests={histogramData?.total || 0}
              optionsList={[
                EventsGroupBy.App,
                EventsGroupBy.EventsOrigin,
                EventsGroupBy.EventType,
                EventsGroupBy.TaskType
              ]}
            />
          }
          yAxisLabelText={'Events count'}
          data={series}
          loading={loading}
          error={error ? <FailedToFetch error={error} /> : null}
        />
      )}
    </LayoutContainer>
  );
};
